import { useIntl } from 'react-intl';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Info, InfoIcon, RotateCw } from 'lucide-react';
import { Box, Button, CircularProgress, Divider, IconButton, Typography, useTheme } from '@mui/material';

import NumberField from 'components/number-field';
import Autocomplete from 'components/autocomplete';

import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { MortalityProfile } from 'views/bluviu-nutrition/features/scenarios-v2/types/mortality-profile';
import { FishGroupSummary } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-result.type';
import { usePostProcessMapper } from 'views/bluviu-nutrition/features/scenarios-v2/hooks/use-post-process-mapper';
import { useScenarioResultState } from 'views/bluviu-nutrition/features/scenarios-v2/context/scenario-result-context';
import { ScenarioHarvestTargetType } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-cage-data.type';
import { PostProcessRequestInput } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-post-process-input.type';

type Props = {
  item: FishGroupSummary;
};

type FishGroupInputsForm = {
  harvestTarget: number;
  mortalityProfile: MortalityProfile | null;
};

const ScenarioFishGroupInputs: React.FC<Props> = ({ item }) => {
  const intl = useIntl();
  const theme = useTheme();
  const snackbarDispatch = useSnackbarDispatch();
  const { buildPostProcessOverride, resetToSiteLevel } = usePostProcessMapper();
  const { editedFishGroups, mortalityProfilesQuery, setEditedFishGroups, updateScenarioPostProcessQuery, getScenarioPostProcessQuery: { data: postProcessData } } = useScenarioResultState();

  const { minAllowedWeek, maxAllowedWeek, minAllowedWeight, maxAllowedWeight, scenarioDefaults, fishGroupSummaries } = postProcessData ?? {};
  
  const isWeekType = item.harvestTargetType?.toLowerCase() === ScenarioHarvestTargetType.WEEK.toLowerCase();
  const isOverriddenInput = editedFishGroups.has(item.fishGroupId);

  const [isEditing, setIsEditing] = useState(false);

  const {
    reset,
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<FishGroupInputsForm>({
    values: {
      harvestTarget: item.harvestTarget,
      mortalityProfile: mortalityProfilesQuery.data?.find((p) => p.id === item.mortalityProfileId) ?? null,
    },
  });

  const updateFishGroup = (body: PostProcessRequestInput | null, onSuccessCallback?: () => void) => {
    if (!body) return;
    updateScenarioPostProcessQuery.mutate(body, {
      onSuccess: () => {
        snackbarDispatch({
          type: SnackbarActionTypes.SUCCESS_SNACKBAR,
          payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
        });
        setIsEditing(false);
        onSuccessCallback?.();
      },
      onError: (error) => {
        snackbarDispatch({
          type: SnackbarActionTypes.ERROR_SNACKBAR,
          payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error },
        });
      },
    });
  };

  const handleCancel = () => {
    reset();
    setIsEditing(false);
  };

  const handleResetToSiteLevel = () => {
    updateFishGroup(resetToSiteLevel(fishGroupSummaries, scenarioDefaults, item.fishGroupId), () =>
      setEditedFishGroups((prev) => new Set([...prev].filter((id) => id !== item.fishGroupId)))
    );
  };

  const handleUpdate = (data: FishGroupInputsForm) => {
    updateFishGroup(
      buildPostProcessOverride(
        {
          useScenarioDefaults: false,
          fishGroupId: item.fishGroupId,
          harvestTargetType: item.harvestTargetType,
          harvestTarget: Number(data.harvestTarget),
          mortalityProfileId: data.mortalityProfile!.id,
        }, fishGroupSummaries, scenarioDefaults
      ),
      () => setEditedFishGroups((prev) => new Set(prev).add(item.fishGroupId))
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Typography variant="h6">Inputs</Typography>
      <NumberField
        dark
        fullWidth
        label={`Harvest target (${isWeekType ? `${minAllowedWeek} - ${maxAllowedWeek}` : `${minAllowedWeight} - ${maxAllowedWeight}`})`}
        disabled={!isEditing}
        endAdornment={<Typography variant="body2">{isWeekType ? 'Weeks' : 'g'}</Typography>}
        disableDecimal={isWeekType}
        controller={{
          name: 'harvestTarget',
          control,
          rules: {
            required: true,
            min: isWeekType ? minAllowedWeek : minAllowedWeight,
            max: isWeekType ? maxAllowedWeek : maxAllowedWeight,
          },
        }}
      />
      <Autocomplete
        dark
        label={'Mortality'}
        disabled={!isEditing}
        disableCloseOnSelect={false}
        options={mortalityProfilesQuery.data ?? []}
        getOptionLabel={(option) => (option as MortalityProfile).name}
        controller={{
          control,
          name: 'mortalityProfile',
          rules: {
            required: true,
          },
        }}
      />
      <Box display="flex" alignItems="center" gap={2}>
        <InfoIcon />
        <Typography variant="body2">
          {isOverriddenInput ? 'Site level inputs overridden.' : 'Showing inputs done on site level.'}
        </Typography>
      </Box>
      <Divider />
      {isEditing ? (
        <Box display="flex" flexDirection="column" gap={3}>
          <Box display="flex" alignItems="flex-start" gap={2} borderRadius={2} p={2} bgcolor={theme.palette.bluViu.main}>
            <Info size={18} color={theme.palette.common.white} style={{ flexShrink: 0, marginTop: 2 }} />
            <Typography variant="body2" color={theme.palette.common.white}>
              Changes will override site level inputs on this fish group.
            </Typography>
          </Box>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" size="small" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSubmit(handleUpdate)}
              disabled={!isDirty || updateScenarioPostProcessQuery.isPending}>
              {updateScenarioPostProcessQuery.isPending ? <CircularProgress size={16} color="inherit" /> : 'Update'}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            {editedFishGroups.has(item.fishGroupId) && (
              <IconButton disableRipple onClick={handleResetToSiteLevel} disabled={updateScenarioPostProcessQuery.isPending}>
                <RotateCw size={16} />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  Reset to site level
                </Typography>
              </IconButton>
            )}
          </Box>
          <Button variant="contained" size="small" onClick={() => setIsEditing(true)}>
            Edit
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(ScenarioFishGroupInputs);
